import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { computeCreaseEdges } from './creaseEdges';
import {
  getFloorMesh3DEnabledProperty,
  getFloorMesh3DColorProperty,
  getFloorMesh3DOpacityProperty,
  getFloorWallSourceProperty,
  getFloorHeatmapEnabledProperty,
  getFloorHeatmapGradientProperty,
  getFloorHeatmapMaxDistanceProperty,
  getFloorHeatmapClipEnabledProperty,
  getFloorHeatmapClipMethodProperty,
  getFloorHeatmapClipToleranceProperty,
  getCeilingPlanePositionProperty,
  hasCeilingPlanePosition,
  getFloorPlanePositionProperty,
  getOutlineOuterRingProperty,
  getOutlineCavitiesProperty,
  getOutlineFloorHeightProperty,
  getRoomPolygonProperty,
  getRoomCeilingProperty,
  getAnnotationColorProperty,
  getAreaPointsProperty,
  getOpeningRoomIdsProperty,
  getOpeningSketchWallEdgeIdProperty,
  getOpeningSketchOffsetProperty,
  getOpeningSketchLengthProperty,
  getOpeningSketchLowerBoundProperty,
  getOpeningSketchHeightProperty,
  getWallSketchNodesProperty,
  getWallSketchEdgesProperty,
  isMeaningfulCeiling,
} from '@pointorama/potree-shared';
import type { Subscription, Coordinate } from '@pointorama/potree-shared';
import { Viewer } from '../viewer/viewer';
import { DefaultColors } from '../defines';
import { buildFloorMesh3D, computeOpeningFrame, FloorMesh3DInput, OpeningInput, Pt2, RoomInput, GroupProvenance, parseGroupTag, openingTag } from './FloorMesh3DBuilder';
import { probeWallDepthsAtLine } from './wallThicknessProbe';
import { FALLBACK_WALL_HEIGHT } from './skeleton/resolveSkeletonHeight';
import { onManifoldReady } from './manifoldCsg';
import { computeOpeningRectangle } from '../../hooks/potree/openingDxfGeometry';
import { extractOutlineRings } from './skeleton/outlineRings';
import { deriveOutlineRingsFromRooms } from './skeleton/roomsOutlineFallback';

/**
 * Identifiers of the annotations on this floor that contribute to the 3D
 * mesh. The hook that creates the mesh resolves them once from the annotation
 * tree and passes them in; the mesh itself stays decoupled from GraphQL.
 *
 * The outline source is resolved merged-first: the floor's WALL_SKETCH
 * (`wallSketchId`) may carry the outline as `role:'OUTLINE'` edge cycles;
 * the legacy OUTLINE annotation (`outlineId`) is the fallback. A floor with
 * neither just renders nothing until one appears (the hook re-creates the
 * mesh when ids change).
 */
export interface FloorMesh3DChildren {
  outlineId: string | null;
  /** The floor's WALL_SKETCH annotation (merged-outline source), if any. */
  wallSketchId: string | null;
  roomIds: string[];
  openingIds: string[];
  /** The floor's OPENING_SKETCHes (parametric on its wall sketch's edges): cut like the openings. */
  openingSketchIds: string[];
}

const SHELL_TAG = '__shell__';

/**
 * One contiguous, single-source slice of the floor mesh, for export. Combines
 * the decoded provenance (shell / room / opening + annotation id) with the
 * triangle range that belongs to it: triangles `[triStart, triStart+triCount)`
 * of the indexed geometry. Several parts may share an id (a room bordering
 * walls in multiple stretches → multiple parts).
 */
export type MeshPart = GroupProvenance & { triStart: number; triCount: number };

/**
 * Deterministic debug color for a provenance tag: shell pieces are grey, every
 * other tag (opening id) gets a saturated hue hashed from its string so the
 * same id always maps to the same color. Writes into `out` and returns it.
 */
function colorForTag(tag: string, out: THREE.Color): THREE.Color {
  if (tag === SHELL_TAG || tag.startsWith(`${SHELL_TAG}#`)) {
    return out.setRGB(0.55, 0.55, 0.55);
  }
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = (hash * 31 + tag.charCodeAt(i)) | 0;
  const hue = ((hash >>> 0) % 360) / 360;
  return out.setHSL(hue, 0.75, 0.55);
}

export class PointoramaFloorMesh3D extends THREE.Object3D {
  private _identifier = '';
  /** Annotation ids gathered by the sync layer. Re-set externally on tree changes. */
  private _children: FloorMesh3DChildren = { outlineId: null, wallSketchId: null, roomIds: [], openingIds: [], openingSketchIds: [] };
  /** The visible mesh — re-created on every rebuild. */
  private mesh: THREE.Mesh | null = null;
  /**
   * Provenance per geometry group, parallel to `mesh.geometry.groups`:
   * `_groupIdByIndex[group.materialIndex]` is the tag that produced that
   * group's triangles — a shell-piece tag (`__shell__` / `__shell__#n`) or an
   * opening annotation id. Use {@link groupIdForTriangle} to resolve a triangle.
   */
  private _groupIdByIndex: string[] = [];
  /** Outline overlay (mirrors the skeleton 3D outline pass) — re-created on rebuild. */
  private outlineLine: LineSegments2 | null = null;
  /**
   * World XY the mesh geometry is built around (the outline's first vertex).
   * The geometry is float32: at UTM northings its steps are ~0.5 m, so it is
   * built relative to this point and the mesh + outline are placed at it.
   */
  private _meshOrigin: Pt2 = { x: 0, y: 0 };
  private outlineGeometry: LineSegmentsGeometry | null = null;
  private outlineMaterial: LineMaterial | null = null;
  /** Per-room color/visibility subscriptions, keyed by room id. */
  private roomColorSubs: Map<string, Subscription> = new Map();
  private roomVisibleSubs: Map<string, Subscription> = new Map();
  /** Geometry-side subscriptions on outline + rooms + openings. */
  private geometrySubs: Subscription[] = [];
  /** Reactive property subscriptions on the floor itself. */
  private _enabledSub?: Subscription;
  private _colorSub?: Subscription;
  private _opacitySub?: Subscription;
  private _heatmapEnabledSub?: Subscription;
  private _heatmapGradientSub?: Subscription;
  private _heatmapMaxDistSub?: Subscription;
  private _heatmapClipEnabledSub?: Subscription;
  private _heatmapClipMethodSub?: Subscription;
  private _heatmapClipToleranceSub?: Subscription;
  private _wallSourceSub?: Subscription;
  private _ceilingPosSub?: Subscription;
  private _floorPosSub?: Subscription;
  /** Subscription on each room's color/visibility cleared on rebuild + on dispose. */

  // Cached property values
  private _mesh3DEnabled = false;
  private _shellColor: THREE.Color = DefaultColors.OBJECTS_THREE.clone();
  private _opacity = 0.7;
  // `_heatmapEnabled` / `_heatmapClipEnabled` are the EFFECTIVE flags of this
  // rooms-based pipeline: raw floor-panel toggle AND `floor_wall_source`
  // set to ROOMS. When the source is WALL_SKETCH the skeleton pipeline runs
  // instead (PointoramaWallSketch follows the same floor properties).
  private _heatmapEnabled = false;
  private _heatmapGradient = 'SPECTRAL';
  private _heatmapMaxDist = 0.02;
  private _heatmapClipEnabled = false;
  private _heatmapClipMethod = 'SHOW_INSIDE';
  private _heatmapClipTolerance = 0.01;
  private _heatmapEnabledRaw = false;
  private _heatmapClipEnabledRaw = false;
  private _wallSourceIsRooms = false;

  /** Most recent wall-edge list from the builder — fed into the SDF shader. */
  private _wallEdges: Array<{ start: { x: number; y: number }; end: { x: number; y: number } }> = [];
  /** Floor Z used for the SDF height-tolerance band — captured from the last build. */
  private _lastFloorZ = 0;
  /** GPU edge texture for the SDF. Disposed on rebuild + dispose. */
  private _edgeTexture: THREE.DataTexture | null = null;
  private _edgeCount = 0;
  /** Debounce flag for SDF regeneration (mirrors the skeleton's pattern). */
  private _sdfRegenerationPending = false;

  private _rebuildPending = false;

  private viewer: Viewer;

  /**
   * Re-apply the SDF when a new point cloud joins the scene mid-session, so
   * the heatmap covers it without needing to toggle off/on. Mirrors
   * PointoramaWallSketch's pointcloud_added listener.
   */
  private _onPointCloudAdded: () => void;

  /** True when the last build rendered an uncut wall because Manifold wasn't
   * ready. We rebuild once it is — see the onManifoldReady subscription. */
  private _openingsDeferred = false;
  /** Unsubscribe from the Manifold-ready notification. */
  private _manifoldReadyUnsub?: () => void;

  constructor(viewer: Viewer) {
    super();
    this.viewer = viewer;
    this.name = 'PointoramaFloorMesh3D';
    this._onPointCloudAdded = () => {
      if ((this._heatmapEnabled || this._heatmapClipEnabled) && this._edgeTexture) {
        this.updateSDFForPointClouds();
      }
    };
    this.viewer.scene.addEventListener('pointcloud_added', this._onPointCloudAdded);

    // If this floor was built before the Manifold WASM module finished loading
    // (only happens on the very first build at page load), its openings were
    // left uncut. Rebuild once Manifold is ready so the doors/windows appear.
    this._manifoldReadyUnsub = onManifoldReady(() => {
      if (this._openingsDeferred) this.scheduleRebuild();
    });
  }

  get identifier(): string {
    return this._identifier;
  }

  set identifier(value: string) {
    this._identifier = value;
    if (value) this.loadReactiveProperties();
  }

  /**
   * Raw provenance tags present in the current mesh, in group order. Prefer
   * {@link meshParts} for export — these are the undecoded tag strings.
   */
  get groupIds(): readonly string[] {
    return this._groupIdByIndex;
  }

  /**
   * Mesh→source linkage for export. One entry per geometry group, each giving
   * the decoded provenance (shell / room / opening + annotation id) and the
   * exact triangle range that belongs to it. Triangles are ordinals into the
   * indexed geometry; `[triStart, triStart + triCount)` are this part's faces.
   *
   * Drives a "split the mesh per room/opening" export, and the inverse lookup
   * ({@link partForTriangle}, {@link trianglesForSource}) is built from it.
   */
  get meshParts(): MeshPart[] {
    const geom = this.mesh?.geometry;
    if (!geom) return [];
    return geom.groups.map((group) => ({
      ...parseGroupTag(this._groupIdByIndex[group.materialIndex ?? 0] ?? ''),
      triStart: group.start / 3,
      triCount: group.count / 3,
    }));
  }

  /** The visible mesh's geometry (indexed), or null before the first build.
   * Pair with {@link meshParts} to export per-source slices. Its positions are
   * relative to {@link meshOrigin}. */
  getMeshGeometry(): THREE.BufferGeometry | null {
    return this.mesh?.geometry ?? null;
  }

  /** World XY the mesh geometry's positions are relative to. */
  get meshOrigin(): Pt2 {
    return this._meshOrigin;
  }

  /**
   * Resolve which shell piece / room / opening a triangle belongs to. `triIndex`
   * is the triangle's ordinal in the indexed geometry — e.g. a Raycaster
   * intersection's `faceIndex`. Returns the decoded provenance, or null if
   * there's no mesh or the triangle falls outside every group.
   */
  partForTriangle(triIndex: number): GroupProvenance | null {
    const geom = this.mesh?.geometry;
    if (!geom) return null;
    const firstIndex = triIndex * 3; // index-buffer offset of the triangle
    for (const group of geom.groups) {
      if (firstIndex >= group.start && firstIndex < group.start + group.count) {
        return parseGroupTag(this._groupIdByIndex[group.materialIndex ?? 0] ?? '');
      }
    }
    return null;
  }

  /**
   * Inverse linkage: every triangle ordinal attributed to a given source. Pass
   * `{ kind: 'room', id }` or `{ kind: 'opening', id }`, or `{ kind: 'shell' }`
   * for all wall faces. Returns a flat list of triangle indices.
   */
  trianglesForSource(query: { kind: GroupProvenance['kind']; id?: string }): number[] {
    const out: number[] = [];
    for (const part of this.meshParts) {
      if (part.kind !== query.kind) continue;
      if (query.id !== undefined && part.id !== query.id) continue;
      for (let t = part.triStart; t < part.triStart + part.triCount; t++) out.push(t);
    }
    return out;
  }

  /** @deprecated use {@link partForTriangle}. Returns the raw tag string. */
  groupIdForTriangle(triIndex: number): string | null {
    const geom = this.mesh?.geometry;
    if (!geom) return null;
    const firstIndex = triIndex * 3;
    for (const group of geom.groups) {
      if (firstIndex >= group.start && firstIndex < group.start + group.count) {
        return this._groupIdByIndex[group.materialIndex ?? 0] ?? null;
      }
    }
    return null;
  }

  /**
   * Update the set of annotation ids on this floor and trigger a rebuild.
   * Called by the sync hook whenever the floor's children change (outline
   * created/deleted, rooms added/removed, openings linked/unlinked).
   */
  setChildren(children: FloorMesh3DChildren): void {
    this._children = children;
    this.resubscribeGeometry();
    this.scheduleRebuild();
  }

  // ─── Reactive property wiring ───────────────────────────────────────

  private loadReactiveProperties(): void {
    if (!this._identifier) return;
    try {
      this._enabledSub?.unsubscribe();
      this._colorSub?.unsubscribe();
      this._opacitySub?.unsubscribe();
      this._heatmapEnabledSub?.unsubscribe();
      this._heatmapGradientSub?.unsubscribe();
      this._heatmapMaxDistSub?.unsubscribe();
      this._heatmapClipEnabledSub?.unsubscribe();
      this._heatmapClipMethodSub?.unsubscribe();
      this._heatmapClipToleranceSub?.unsubscribe();
      this._wallSourceSub?.unsubscribe();
      this._ceilingPosSub?.unsubscribe();
      this._floorPosSub?.unsubscribe();

      const enabledProp = getFloorMesh3DEnabledProperty(this._identifier);
      this._mesh3DEnabled = enabledProp.value;
      this._enabledSub = enabledProp.subscribeToChanges((value: boolean) => {
        this._mesh3DEnabled = value;
        this.applyVisibility();
        if (value && !this.mesh && this._wallSourceIsRooms) this.scheduleRebuild();
      });

      const colorProp = getFloorMesh3DColorProperty(this._identifier);
      this._shellColor = new THREE.Color(colorProp.value);
      this._colorSub = colorProp.subscribeToChanges((value: string) => {
        this._shellColor = new THREE.Color(value);
        this.applyShellColor();
      });

      const opacityProp = getFloorMesh3DOpacityProperty(this._identifier);
      this._opacity = opacityProp.value;
      this._opacitySub = opacityProp.subscribeToChanges((value: number) => {
        this._opacity = value;
        this.applyOpacity();
      });

      // ─── Source switch (rooms vs wall sketch) ───────────────────────
      // `floor_wall_source` decides which pipeline renders the floor's
      // 3D AND measures the heatmap/clipping: ROOMS drives THIS outline⊖rooms
      // mesh, WALL_SKETCH hands everything to the skeleton
      // (PointoramaWallSketch follows the same floor properties) and this one
      // goes dark. Loaded before the enable toggles so the effective flags
      // below resolve correctly.
      const wallSourceProp = getFloorWallSourceProperty(this._identifier);
      this._wallSourceIsRooms = wallSourceProp.value === 'ROOMS';
      this._wallSourceSub = wallSourceProp.subscribeToChanges((value: string) => {
        this._wallSourceIsRooms = value === 'ROOMS';
        this._heatmapEnabled = this._wallSourceIsRooms && this._heatmapEnabledRaw;
        this._heatmapClipEnabled = this._wallSourceIsRooms && this._heatmapClipEnabledRaw;
        // 3D mesh: switching to ROOMS (re)builds it, switching away drops it
        // (the skeleton walls render the floor's 3D instead).
        if (this._wallSourceIsRooms) {
          if (this._mesh3DEnabled && !this.mesh) this.scheduleRebuild();
        } else {
          this.disposeMesh();
        }
        this.applyVisibility();
        if (this._heatmapEnabled || this._heatmapClipEnabled) {
          if (this._wallEdges.length === 0) this.scheduleRebuild();
          else this.scheduleSDFRegeneration();
        } else {
          // Release the material slot for the wall sketch pipeline.
          this.clearSDFFromPointClouds();
        }
      });

      const heatmapProp = getFloorHeatmapEnabledProperty(this._identifier);
      this._heatmapEnabledRaw = heatmapProp.value;
      this._heatmapEnabled = this._wallSourceIsRooms && this._heatmapEnabledRaw;
      this._heatmapEnabledSub = heatmapProp.subscribeToChanges((value: boolean) => {
        this._heatmapEnabledRaw = value;
        this._heatmapEnabled = this._wallSourceIsRooms && value;
        if (this._heatmapEnabled || this._heatmapClipEnabled) {
          // If no wall edges have been computed yet (3D mesh has never
          // been built), kick a build first — `scheduleRebuild` is a
          // no-op when nothing wants the geometry, so we have to call it
          // explicitly here now that the heatmap is on.
          if (this._wallEdges.length === 0) this.scheduleRebuild();
          else this.scheduleSDFRegeneration();
        } else {
          this.updateSDFForPointClouds();
        }
      });

      const heatmapGradientProp = getFloorHeatmapGradientProperty(this._identifier);
      this._heatmapGradient = heatmapGradientProp.value;
      this._heatmapGradientSub = heatmapGradientProp.subscribeToChanges((value: string) => {
        this._heatmapGradient = value;
        this.updateSDFForPointClouds();
      });

      const heatmapMaxDistProp = getFloorHeatmapMaxDistanceProperty(this._identifier);
      this._heatmapMaxDist = heatmapMaxDistProp.value;
      this._heatmapMaxDistSub = heatmapMaxDistProp.subscribeToChanges((value: number) => {
        this._heatmapMaxDist = value;
        this.updateSDFForPointClouds();
      });

      // The vertical band is NOT a panel property — it derives from the
      // floor's own height range (see getHeatmapHeightTolerance); the
      // floor/ceiling plane subscriptions below re-trigger the rebuild that
      // re-pushes it.

      const heatmapClipEnabledProp = getFloorHeatmapClipEnabledProperty(this._identifier);
      this._heatmapClipEnabledRaw = heatmapClipEnabledProp.value;
      this._heatmapClipEnabled = this._wallSourceIsRooms && this._heatmapClipEnabledRaw;
      this._heatmapClipEnabledSub = heatmapClipEnabledProp.subscribeToChanges((value: boolean) => {
        this._heatmapClipEnabledRaw = value;
        this._heatmapClipEnabled = this._wallSourceIsRooms && value;
        if (this._heatmapClipEnabled || this._heatmapEnabled) {
          if (this._wallEdges.length === 0) this.scheduleRebuild();
          else this.scheduleSDFRegeneration();
        } else {
          this.updateSDFForPointClouds();
        }
      });

      const heatmapClipMethodProp = getFloorHeatmapClipMethodProperty(this._identifier);
      this._heatmapClipMethod = heatmapClipMethodProp.value;
      this._heatmapClipMethodSub = heatmapClipMethodProp.subscribeToChanges((value: string) => {
        this._heatmapClipMethod = value;
        this.updateSDFForPointClouds();
      });

      const heatmapClipToleranceProp = getFloorHeatmapClipToleranceProperty(this._identifier);
      this._heatmapClipTolerance = heatmapClipToleranceProp.value;
      this._heatmapClipToleranceSub = heatmapClipToleranceProp.subscribeToChanges((value: number) => {
        this._heatmapClipTolerance = value;
        this.updateSDFForPointClouds();
      });

      // Floor + ceiling plane Z give the shell extrusion bounds. The user can
      // update these via the floor-detection tool; re-trigger a rebuild.
      const ceilingPosProp = getCeilingPlanePositionProperty(this._identifier);
      this._ceilingPosSub = ceilingPosProp.subscribeToChanges(() => this.scheduleRebuild());

      const floorPosProp = getFloorPlanePositionProperty(this._identifier);
      this._floorPosSub = floorPosProp.subscribeToChanges(() => this.scheduleRebuild());

      this.applyVisibility();
      if (this._mesh3DEnabled && this._wallSourceIsRooms) this.scheduleRebuild();
      // Mirrors PointoramaWallSketch.loadReactiveProperties: if heatmap or
      // clipping starts already enabled (persisted state), kick the SDF
      // pipeline once now. The mesh's first rebuild will populate
      // `_wallEdges`, which `regenerateSDF` reads.
      if (this._heatmapEnabled || this._heatmapClipEnabled) this.scheduleSDFRegeneration();
    } catch {
      // Global property service not initialized yet.
    }
  }

  /**
   * Subscribe to every geometry-bearing property whose change should
   * invalidate the mesh. Called by setChildren() — unsubscribes old listeners
   * first so removed children don't keep the mesh subscribed.
   */
  private resubscribeGeometry(): void {
    for (const s of this.geometrySubs) s.unsubscribe();
    this.geometrySubs = [];

    try {
      if (this._children.outlineId) {
        const id = this._children.outlineId;
        this.geometrySubs.push(getOutlineOuterRingProperty(id).subscribeToChanges(() => this.scheduleRebuild()));
        this.geometrySubs.push(getOutlineCavitiesProperty(id).subscribeToChanges(() => this.scheduleRebuild()));
        this.geometrySubs.push(getOutlineFloorHeightProperty(id).subscribeToChanges(() => this.scheduleRebuild()));
      }
      if (this._children.wallSketchId) {
        // Merged outline: the rings live in the sketch's node/edge graph, so
        // sketch edits (incl. cross-tab) must invalidate the mesh too.
        const id = this._children.wallSketchId;
        this.geometrySubs.push(getWallSketchNodesProperty(id).subscribeToChanges(() => this.scheduleRebuild()));
        this.geometrySubs.push(getWallSketchEdgesProperty(id).subscribeToChanges(() => this.scheduleRebuild()));
      }
      for (const id of this._children.roomIds) {
        this.geometrySubs.push(getRoomPolygonProperty(id).subscribeToChanges(() => this.scheduleRebuild()));
        this.geometrySubs.push(getRoomCeilingProperty(id).subscribeToChanges(() => this.scheduleRebuild()));
      }
      for (const id of this._children.openingIds) {
        // Opening footprint is encoded as AREA points + per-opening height,
        // so polygon changes propagate via the AREA point property.
        this.geometrySubs.push(getAreaPointsProperty(id).subscribeToChanges(() => this.scheduleRebuild()));
        // Opening → room linkage may be set or changed *after* the OPENING
        // annotation appears (the sync hook gives us every opening in the
        // project), so re-resolve on linkage change. This is what triggers
        // a rebuild when a brand-new opening's `opening_room_ids` finally
        // gets populated with a room on this floor.
        this.geometrySubs.push(getOpeningRoomIdsProperty(id).subscribeToChanges(() => this.scheduleRebuild()));
      }
      for (const id of this._children.openingSketchIds) {
        // An opening sketch's hole follows its span on the edge and its vertical extent.
        for (const prop of [getOpeningSketchWallEdgeIdProperty(id), getOpeningSketchOffsetProperty(id), getOpeningSketchLengthProperty(id), getOpeningSketchLowerBoundProperty(id), getOpeningSketchHeightProperty(id)]) {
          this.geometrySubs.push(prop.subscribeToChanges(() => this.scheduleRebuild()));
        }
      }
    } catch {
      // Property service not initialized.
    }
  }

  // ─── Rebuild ────────────────────────────────────────────────────────

  private scheduleRebuild(): void {
    if (this._rebuildPending) return;
    this._rebuildPending = true;
    requestAnimationFrame(() => {
      this._rebuildPending = false;
      // Skip only when nothing wants the geometry. The heatmap and the
      // point-cloud clip both depend on `_wallEdges`, which only gets
      // populated by a rebuild, so they need to drive a rebuild even when
      // the visible 3D mesh is off.
      if (!this._mesh3DEnabled && !this._heatmapEnabled && !this._heatmapClipEnabled) return;
      this.rebuild();
    });
  }

  private rebuild(): void {
    this.disposeMesh();
    // Rooms alone are enough: the outline is derived around them (see resolveOutlineInput).
    if (!this._children.outlineId && !this._children.wallSketchId && this._children.roomIds.length === 0) return;

    let input: FloorMesh3DInput;
    try {
      input = this.collectInput(this._children.roomIds, this._children.openingIds);
    } catch (err) {
      console.warn('FloorMesh3D: failed to collect input', err);
      return;
    }
    if (input.outerRing.length < 3) return;

    // Built relative to the outline's first vertex (see `_meshOrigin`): the
    // input is shifted while it is still float64, never the float32 result.
    const origin = { x: input.outerRing[0].x, y: input.outerRing[0].y };
    this._meshOrigin = origin;
    const result = buildFloorMesh3D(this.localizeInput(input, origin));
    // Remember whether openings were left uncut (Manifold not ready). The
    // onManifoldReady subscription rebuilds us once it is.
    this._openingsDeferred = result.openingsDeferred;
    // Capture inputs the heatmap shader path needs (wall edges + floor Z).
    // These get pushed to PointCloudMaterial via the SDF regeneration call,
    // which works in world space: shift them back.
    const toWorld = (p: Pt2): Pt2 => ({ x: p.x + origin.x, y: p.y + origin.y });
    this._wallEdges = result.wallEdges.map((edge) => ({ start: toWorld(edge.start), end: toWorld(edge.end) }));
    this._lastFloorZ = input.floorZ;
    if (this._heatmapEnabled || this._heatmapClipEnabled) this.scheduleSDFRegeneration();

    // Visible 3D mesh is optional — the heatmap and clip work off
    // `_wallEdges` alone, so we can skip the mesh + outline overlay when
    // only the heatmap is on. This avoids paying the CSG / triangulation
    // visual cost when the user just wants point-cloud SDF coloring.
    // With the source switched to WALL_SKETCH the floor's 3D renders as the
    // skeleton walls, so skip the visible mesh then too (the wall edges
    // above still feed any heatmap).
    if (!this._mesh3DEnabled || !this._wallSourceIsRooms) {
      result.geometry.dispose();
      return;
    }

    // Geometry may be indexed (Manifold cut result) or non-indexed (uncut
    // pre-geom). Check the position attribute, which exists either way.
    const positionAttr = result.geometry.getAttribute('position');
    if (!positionAttr || positionAttr.count === 0) {
      result.geometry.dispose();
      return;
    }

    // Single material — once openings start cutting, the per-group attribution
    // breaks down anyway (CSG with useGroups=false strips groups). We render
    // the whole shell in one color; room rendering needs a different
    // strategy when we bring it back.
    const shellMaterial = this.materialForTag(SHELL_TAG);
    this.mesh = new THREE.Mesh(result.geometry, shellMaterial);
    this.mesh.name = 'FloorMesh3DMesh';
    this.mesh.position.set(origin.x, origin.y, 0);
    // Render fills before lines so polygonOffset + renderOrder keep edges crisp.
    this.mesh.renderOrder = 1;
    this.add(this.mesh);

    // Outline overlay. `computeCreaseEdges` reconstructs face adjacency by
    // clustering coincident vertices within a tolerance, so it works on both
    // the indexed Manifold cut result and the non-indexed uncut pre-geom — no
    // pre-snap / mergeVertices needed (grid-snapping was the source of the
    // earlier "random missing edges": it pushed the two copies of a shared
    // corner onto different cells, the weld failed, and the shared edge was
    // dropped as an unpaired boundary).
    //
    // 10° threshold: faces whose normals diverge by more than that keep
    // their shared edge; flatter neighbours (incl. CSG's antiparallel-wound
    // coplanar triangles) are treated as one surface and hidden.
    const filteredPositions = computeCreaseEdges(result.geometry, 10);

    if (filteredPositions.length > 0) {
      const rendererSize = new THREE.Vector2();
      this.viewer.renderer.getSize(rendererSize);

      this.outlineGeometry = new LineSegmentsGeometry();
      this.outlineGeometry.setPositions(filteredPositions);
      this.outlineMaterial = new LineMaterial({
        color: 0x000000,
        linewidth: 1.5,
        resolution: rendererSize,
        depthTest: true,
      });
      this.outlineLine = new LineSegments2(this.outlineGeometry, this.outlineMaterial);
      this.outlineLine.position.set(origin.x, origin.y, 0);
      this.outlineLine.renderOrder = 2;
      this.outlineLine.computeLineDistances();
      this.add(this.outlineLine);
    }

    // Provenance: tag for each geometry group (shell piece / opening id),
    // queryable via groupIdForTriangle / groupIds.
    this._groupIdByIndex = result.groupIdByIndex;

    // Subscribe to per-room color/visibility for the materials we just made.
    this.resubscribeRoomMaterials(result.groupIdByIndex);
    // Re-apply debug group colorize if it was left on (survives rebuilds).
    if (this._debugColorizeGroups) this.colorizeGroups();
    this.applyVisibility();
  }

  /**
   * The floor's outline rings + their source Z: the WALL_SKETCH's merged
   * `role:'OUTLINE'` cycles first (largest component wins, matching the
   * single-outline convention), the legacy OUTLINE annotation's properties as
   * fallback. Throws when neither yields a usable ring (legacy path only —
   * merged extraction can't throw).
   */
  private resolveOutlineInput(): { outerRing: Pt2[]; cavities: Pt2[][]; ringZ: number | null } {
    const coordToPt = (c: Coordinate): Pt2 => ({ x: c.x, y: c.y });

    const wallSketchId = this._children.wallSketchId;
    if (wallSketchId) {
      const components = extractOutlineRings(getWallSketchNodesProperty(wallSketchId).value, getWallSketchEdgesProperty(wallSketchId).value);
      if (components.length > 0) {
        let best = components[0];
        let bestArea = -Infinity;
        for (const component of components) {
          let area = 0;
          const ring = component.outerRing;
          for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            area += ring[j].x * ring[i].y - ring[i].x * ring[j].y;
          }
          area = Math.abs(area / 2);
          if (area > bestArea) {
            bestArea = area;
            best = component;
          }
        }
        return {
          outerRing: best.outerRing.map(coordToPt),
          cavities: best.holes.map((ring) => ring.map(coordToPt)),
          ringZ: best.outerRing[0]?.z ?? null,
        };
      }
    }

    const outlineId = this._children.outlineId;
    if (outlineId) {
      const floorHeight = getOutlineFloorHeightProperty(outlineId).value;
      const outerRing = getOutlineOuterRingProperty(outlineId).value.map(coordToPt);
      if (outerRing.length >= 3) {
        return {
          outerRing,
          cavities: getOutlineCavitiesProperty(outlineId).value.map((ring: Coordinate[]) => ring.map(coordToPt)),
          ringZ: Number.isFinite(floorHeight) ? floorHeight : null,
        };
      }
    }

    // No outline drawn or merged: the as-built band around the rooms themselves
    // (a floor converted from its rooms alone), largest component first. Its Z
    // is the floor plane's (the caller's fallback).
    const derived = deriveOutlineRingsFromRooms(this.viewer.scene.rooms.filter((room) => room.groupIdentifier === this._identifier && !!room.identifier));
    if (derived.length === 0) return { outerRing: [], cavities: [], ringZ: null };
    let best = derived[0];
    let bestArea = -Infinity;
    for (const component of derived) {
      let area = 0;
      const ring = component.outerRing;
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) area += ring[j].x * ring[i].y - ring[i].x * ring[j].y;
      area = Math.abs(area / 2);
      if (area > bestArea) {
        bestArea = area;
        best = component;
      }
    }
    return { outerRing: best.outerRing, cavities: best.holes, ringZ: null };
  }

  /**
   * Read all geometry inputs out of the property registry. Throws if the
   * outline has no usable ring; callers swallow and skip the rebuild.
   */
  /**
   * The mesh input relative to `origin`: every XY the build turns into float32
   * geometry is shifted while still float64. Z stays absolute (floor heights
   * are small). The floor's OPENING_SKETCHes are cut here too: the wall sketch
   * builds their cutters from its edges (span, vertical extent), with the wall
   * faces measured on the rooms and the outline (the same band this mesh is
   * made of), around the same origin.
   */
  private localizeInput(input: FloorMesh3DInput, origin: Pt2): FloorMesh3DInput {
    const local = (p: Pt2): Pt2 => ({ x: p.x - origin.x, y: p.y - origin.y });
    const wallSketch = this._children.wallSketchId ? this.viewer.scene.wallSketches.find((sketch) => sketch.identifier === this._children.wallSketchId) : undefined;
    return {
      ...input,
      outerRing: input.outerRing.map(local),
      cavities: input.cavities.map((ring) => ring.map(local)),
      rooms: input.rooms.map((room) => ({ ...room, polygon: room.polygon.map(local) })),
      openings: input.openings.map((opening) => ({ ...opening, polygon3D: opening.polygon3D.map((p) => ({ x: p.x - origin.x, y: p.y - origin.y, z: p.z })) })),
      extraCutters: wallSketch ? wallSketch.buildOpeningCutters(input.floorZ, { measuredFaces: true, tag: openingTag, origin }) : [],
    };
  }

  private collectInput(roomIds: string[], openingIds: string[]): FloorMesh3DInput {
    const coordToPt = (c: Coordinate): Pt2 => ({ x: c.x, y: c.y });

    const { outerRing, cavities, ringZ } = this.resolveOutlineInput();

    // Floor Z: prefer the ring's own Z (outline floorHeight / merged-ring
    // node Z), fall back to floor_plane_position.z.
    const floorZ = ringZ !== null && Number.isFinite(ringZ) ? ringZ : getFloorPlanePositionProperty(this._identifier).value.z;
    // Shell ceiling Z: floor's detected TRUE ceiling plane (per-room ceilings
    // come from each room's own ceiling property). Under true-ceiling
    // semantics stacked storeys show an intentional slab-thickness gap
    // between shells — the mesh represents the interior, not the slab.
    const ceilingZ = getCeilingPlanePositionProperty(this._identifier).value.z;
    const shellCeilingZ = isMeaningfulCeiling(floorZ, ceilingZ) ? ceilingZ : floorZ + FALLBACK_WALL_HEIGHT;

    const rooms: RoomInput[] = [];
    for (const id of roomIds) {
      const poly = getRoomPolygonProperty(id).value.map(coordToPt);
      if (poly.length < 3) continue;
      const roomCeiling = getRoomCeilingProperty(id).value;
      const roomCeilingZ = roomCeiling?.position?.z;
      rooms.push({ id, polygon: poly, ceilingZ: typeof roomCeilingZ === 'number' && roomCeilingZ > floorZ ? roomCeilingZ : shellCeilingZ });
    }

    // Only openings linked (via `opening_room_ids`) to a room on this floor
    // become cutters. The sync hook passes every opening in the project so
    // the mesh can react to linkage changes; we filter here.
    const roomIdSet = new Set(roomIds);
    const openings: OpeningInput[] = [];
    for (const id of openingIds) {
      let linkedRoomIds: string[] = [];
      try {
        linkedRoomIds = getOpeningRoomIdsProperty(id).value;
      } catch {
        continue;
      }
      if (!linkedRoomIds.some((rid) => roomIdSet.has(rid))) continue;

      const pts = getAreaPointsProperty(id).value;
      if (!pts || pts.length < 3) continue;

      // Pull wall thickness from the rectangle-fit's short axis when
      // available — that's the wall's actual thickness derived from the
      // outline + room polygons. When the fit can't run (typically near
      // corners) the old blanket 1m box is NOT harmless: it crosses the
      // junction and carves the adjacent wall. Probe the real boundaries
      // per side instead, shifting the cutter for off-center footprints.
      let wallThickness = 1.0;
      let wallNormShift = 0;
      const rect = computeOpeningRectangle(id, [outerRing, ...cavities].filter((ring) => ring.length >= 3));
      if (!rect.fallback && rect.corners.length === 4) {
        const c = rect.corners;
        const d01 = Math.hypot(c[1].x - c[0].x, c[1].y - c[0].y);
        const d12 = Math.hypot(c[2].x - c[1].x, c[2].y - c[1].y);
        wallThickness = Math.min(d01, d12);
      } else {
        // Same frame convention as buildOpeningCutter (both derive it from
        // computeOpeningFrame on the same points), so the shift's sign lines up.
        const frame = computeOpeningFrame(pts.map((p) => ({ x: p.position.x, y: p.position.y })));
        if (frame) {
          const rings = [outerRing, ...cavities, ...rooms.map((r) => r.polygon)].filter((ring) => ring.length >= 3);
          const depths = probeWallDepthsAtLine(frame.center, frame.wallDir, frame.wallNorm, frame.halfWidth, rings);
          wallThickness = depths.positive + depths.negative;
          wallNormShift = (depths.positive - depths.negative) / 2;
        }
      }

      openings.push({
        id,
        polygon3D: pts.map((p) => ({ x: p.position.x, y: p.position.y, z: p.position.z })),
        wallThickness,
        wallNormShift,
      });
    }

    // World space; the opening sketches' cutters are added by localizeInput,
    // around the mesh origin.
    return { outerRing, cavities, floorZ, ceilingZ: shellCeilingZ, rooms, openings };
  }

  private materialForTag(tag: string): THREE.MeshBasicMaterial {
    const isShell = tag === SHELL_TAG || tag.startsWith(`${SHELL_TAG}#`);
    let color: THREE.Color;
    if (isShell) {
      color = this._shellColor.clone();
    } else {
      try {
        color = new THREE.Color(getAnnotationColorProperty(tag).value);
      } catch {
        color = this._shellColor.clone();
      }
    }
    // Matches the skeleton's 3D shading exactly (PointoramaWallSketch.createExtrudedWalls):
    // unlit basic material + polygonOffset so the outline overlay sits cleanly on top.
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: this._opacity < 1.0,
      opacity: this._opacity,
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: true,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
    });
    mat.name = `FloorMesh3D:${tag}`;
    // Hide room geometry for now — the rooms are still tagged + present in
    // the geometry (we'll need them later for opening attribution and
    // per-room queries), they just don't render. Toggle this back to true
    // when per-room rendering is wanted.
    if (!isShell) mat.visible = false;
    return mat;
  }

  private resubscribeRoomMaterials(_groupIdByIndex: string[]): void {
    for (const s of this.roomColorSubs.values()) s.unsubscribe();
    for (const s of this.roomVisibleSubs.values()) s.unsubscribe();
    this.roomColorSubs.clear();
    this.roomVisibleSubs.clear();

    // No-op for now: single-material mesh with no per-room attribution. The
    // method stays as a placeholder for the per-room rendering path.
  }

  // ─── Display state ──────────────────────────────────────────────────

  /** When true, faces are colored by their provenance group (debug only). */
  private _debugColorizeGroups = false;

  /**
   * Debug aid: color each provenance group (shell piece / opening) a distinct
   * hue so the per-id attribution is visible. Toggling re-applies on the next
   * rebuild too (the flag is read in `colorizeGroups`). Pass false to restore
   * the normal flat shell color.
   */
  setDebugColorizeGroups(on: boolean): void {
    this._debugColorizeGroups = on;
    this.colorizeGroups();
  }

  /** True if group colorize debug is currently on. */
  get debugColorizeGroups(): boolean {
    return this._debugColorizeGroups;
  }

  /** Per-group debug materials created while colorize is on, so we can dispose
   * them when toggling off (the geometry groups index into this array). */
  private _debugGroupMaterials: THREE.MeshBasicMaterial[] = [];

  /**
   * Apply (or remove) per-group debug coloring on the current mesh according to
   * `_debugColorizeGroups`. Uses a material array keyed by each group's
   * materialIndex (so colors are crisp at group seams — no vertex-color bleed),
   * with a deterministic hue per tag. The single shell material is restored
   * when toggled off.
   */
  private colorizeGroups(): void {
    const mesh = this.mesh;
    if (!mesh) return;

    // Dispose whatever materials the mesh currently owns (single shell material
    // or a previous debug array) before swapping.
    const current = mesh.material;
    if (Array.isArray(current)) current.forEach((m) => m.dispose());
    else current.dispose();
    this._debugGroupMaterials = [];

    if (!this._debugColorizeGroups) {
      // Restore the normal single shell material.
      mesh.material = this.materialForTag(SHELL_TAG);
      return;
    }

    // One material per geometry group, colored by that group's tag. Build the
    // array indexed by materialIndex so three.js renders each group with it.
    const maxIndex = mesh.geometry.groups.reduce((m, g) => Math.max(m, g.materialIndex ?? 0), 0);
    const materials: THREE.MeshBasicMaterial[] = [];
    const c = new THREE.Color();
    for (let i = 0; i <= maxIndex; i++) {
      colorForTag(this._groupIdByIndex[i] ?? '', c);
      materials[i] = new THREE.MeshBasicMaterial({
        color: c.clone(),
        side: THREE.DoubleSide,
        transparent: this._opacity < 1.0,
        opacity: this._opacity,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      });
    }
    this._debugGroupMaterials = materials;
    mesh.material = materials;
  }

  private applyVisibility(): void {
    this.visible = this._mesh3DEnabled && this._wallSourceIsRooms && this.mesh !== null;
  }

  private applyShellColor(): void {
    if (!this.mesh) return;
    // While the debug colorize is on the mesh uses a material array; leave it
    // alone so the per-id colors stand.
    if (Array.isArray(this.mesh.material)) return;
    (this.mesh.material as THREE.MeshBasicMaterial).color = this._shellColor.clone();
  }

  private applyOpacity(): void {
    if (!this.mesh) return;
    const mats = Array.isArray(this.mesh.material) ? this.mesh.material : [this.mesh.material];
    for (const mat of mats) {
      (mat as THREE.MeshBasicMaterial).opacity = this._opacity;
      (mat as THREE.MeshBasicMaterial).transparent = this._opacity < 1.0;
    }
  }

  /**
   * Called once per frame by the viewer (same path as PointoramaWallSketch.update).
   * Keeps the LineMaterial's resolution uniform in sync with the canvas so
   * the outline doesn't render at the placeholder resolution.
   */
  update(): void {
    if (!this.outlineMaterial) return;
    const size = this.viewer.renderer.getSize(new THREE.Vector2());
    this.outlineMaterial.resolution.set(size.width, size.height);
  }

  // ─── Heatmap (point cloud SDF) ──────────────────────────────────────
  //
  // Mirrors PointoramaWallSketch's SDF pipeline: gather wall edges from the
  // last build, pack them into a 1×N RGBA-float texture, then push the
  // texture + SDF parameters into every point cloud's PointCloudMaterial
  // via `setFloorMesh3DEdges`. The shader has a parallel uniform set
  // (`floorMesh3DSDF*`) so it can run alongside the skeleton SDF.

  private scheduleSDFRegeneration(): void {
    if (this._sdfRegenerationPending) return;
    this._sdfRegenerationPending = true;
    requestAnimationFrame(() => {
      this._sdfRegenerationPending = false;
      this.regenerateSDF();
    });
  }

  private regenerateSDF(): void {
    if (!this._heatmapEnabled && !this._heatmapClipEnabled) {
      this.clearSDFFromPointClouds();
      return;
    }
    if (this._wallEdges.length === 0) {
      // The mesh hasn't been built yet — `_wallEdges` will populate on the
      // first rebuild and that will re-trigger regeneration.
      this.clearSDFFromPointClouds();
      return;
    }

    const segmentCount = this._wallEdges.length;
    const textureData = new Float32Array(segmentCount * 4);
    for (let i = 0; i < segmentCount; i++) {
      const seg = this._wallEdges[i];
      textureData[i * 4 + 0] = seg.start.x;
      textureData[i * 4 + 1] = seg.start.y;
      textureData[i * 4 + 2] = seg.end.x;
      textureData[i * 4 + 3] = seg.end.y;
    }

    if (this._edgeTexture) this._edgeTexture.dispose();
    this._edgeTexture = new THREE.DataTexture(textureData, segmentCount, 1, THREE.RGBAFormat, THREE.FloatType);
    this._edgeTexture.internalFormat = 'RGBA32F';
    this._edgeTexture.minFilter = THREE.NearestFilter;
    this._edgeTexture.magFilter = THREE.NearestFilter;
    this._edgeTexture.wrapS = THREE.ClampToEdgeWrapping;
    this._edgeTexture.wrapT = THREE.ClampToEdgeWrapping;
    this._edgeTexture.needsUpdate = true;
    this._edgeCount = segmentCount;

    this.updateSDFForPointClouds();
  }

  private getGradientIndex(name: string): number {
    const map: Record<string, number> = {
      SPECTRAL: 0,
      PLASMA: 1,
      YELLOW_GREEN: 2,
      VIRIDIS: 3,
      INFERNO: 4,
      GRAYSCALE: 5,
      TURBO: 6,
      RAINBOW: 7,
      CONTOUR: 8,
    };
    return map[name] ?? 0;
  }

  private updateSDFForPointClouds(): void {
    const needsSDF = this._heatmapEnabled || this._heatmapClipEnabled;
    if (!needsSDF || !this._edgeTexture) {
      this.clearSDFFromPointClouds();
      return;
    }
    const pointclouds = this.viewer?.scene?.pointclouds || [];
    const gradientIndex = this._heatmapEnabled ? this.getGradientIndex(this._heatmapGradient) : -1;
    const clipMethodMap: Record<string, number> = { SHOW_INSIDE: 2, SHOW_OUTSIDE: 3 };
    const clipMethodValue = this._heatmapClipEnabled ? (clipMethodMap[this._heatmapClipMethod] ?? 0) : 0;

    for (const pc of pointclouds) {
      const material = pc.material as unknown as {
        floorMesh3DSDFEnabled?: boolean;
        setFloorMesh3DEdges?: (tex: THREE.Texture | null, count: number, maxDist: number, gradient: number, floorHeight: number, heightTol: number, clipMethod: number, clipTolerance: number) => void;
      };
      if (material && material.setFloorMesh3DEdges) {
        // IMPORTANT: enable the define first so `updateShaderSource` rebuilds
        // the vertex shader with the `#define floor_mesh3d_sdf_enabled` set.
        // Setting uniforms alone wouldn't compile in the new define, so the
        // shader would never run our SDF block. The actual per-frame uniform
        // upload to the GL program happens in `PotreeRenderer.ts` — Potree
        // doesn't go through three.js' standard material uniform pipeline.
        material.floorMesh3DSDFEnabled = true;
        // Symmetric shader gate → pass the band midpoint with half the
        // floor→ceiling range, covering exactly floor→ceiling (see the wall
        // sketch's updateSDFForPointClouds for the same trick).
        const bandHalf = this.getHeatmapHeightTolerance() / 2;
        material.setFloorMesh3DEdges(this._edgeTexture, this._edgeCount, this._heatmapMaxDist, gradientIndex, this._lastFloorZ + bandHalf, bandHalf, clipMethodValue, this._heatmapClipTolerance);
      }
    }
  }

  /**
   * Vertical band (m from the floor plane) within which the heatmap /
   * clipping apply. Derived from the floor's own height range (ceiling −
   * floor), mirroring the wall sketch pipeline, rather than a user-set
   * tolerance. Falls back to {@link FALLBACK_WALL_HEIGHT} when the floor
   * has no stored ceiling. The floor/ceiling plane subscriptions re-trigger
   * the rebuild + SDF push when the range changes.
   */
  private getHeatmapHeightTolerance(): number {
    try {
      if (hasCeilingPlanePosition(this._identifier)) {
        const ceilingZ = getCeilingPlanePositionProperty(this._identifier).value.z;
        const range = ceilingZ - this._lastFloorZ;
        if (range > 0.1) return range;
      }
    } catch {
      /* property service not ready */
    }
    return FALLBACK_WALL_HEIGHT;
  }

  private clearSDFFromPointClouds(): void {
    const pointclouds = this.viewer?.scene?.pointclouds || [];
    for (const pc of pointclouds) {
      const material = pc.material as unknown as { clearFloorMesh3DSDF?: () => void };
      if (material && material.clearFloorMesh3DSDF) material.clearFloorMesh3DSDF();
    }
    if (this._edgeTexture) {
      this._edgeTexture.dispose();
      this._edgeTexture = null;
      this._edgeCount = 0;
    }
  }

  // ─── Cleanup ────────────────────────────────────────────────────────

  private disposeMesh(): void {
    if (this.outlineLine) {
      this.remove(this.outlineLine);
      this.outlineLine = null;
    }
    if (this.outlineGeometry) {
      this.outlineGeometry.dispose();
      this.outlineGeometry = null;
    }
    if (this.outlineMaterial) {
      this.outlineMaterial.dispose();
      this.outlineMaterial = null;
    }
    if (!this.mesh) return;
    this.remove(this.mesh);
    this.mesh.geometry.dispose();
    // material is either the single shell material or the debug array (which is
    // the same as `_debugGroupMaterials`); disposing the live one covers both.
    const mat = this.mesh.material;
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
    else mat.dispose();
    this._debugGroupMaterials = [];
    this.mesh = null;
  }

  dispose(): void {
    this._enabledSub?.unsubscribe();
    this._colorSub?.unsubscribe();
    this._opacitySub?.unsubscribe();
    this._heatmapEnabledSub?.unsubscribe();
    this._heatmapGradientSub?.unsubscribe();
    this._heatmapMaxDistSub?.unsubscribe();
    this._heatmapClipEnabledSub?.unsubscribe();
    this._heatmapClipMethodSub?.unsubscribe();
    this._heatmapClipToleranceSub?.unsubscribe();
    this._wallSourceSub?.unsubscribe();
    this._ceilingPosSub?.unsubscribe();
    this._floorPosSub?.unsubscribe();
    for (const s of this.geometrySubs) s.unsubscribe();
    this.geometrySubs = [];
    for (const s of this.roomColorSubs.values()) s.unsubscribe();
    for (const s of this.roomVisibleSubs.values()) s.unsubscribe();
    this.roomColorSubs.clear();
    this.roomVisibleSubs.clear();
    this.clearSDFFromPointClouds();
    if (this._onPointCloudAdded) this.viewer.scene.removeEventListener('pointcloud_added', this._onPointCloudAdded);
    this._manifoldReadyUnsub?.();
    this.disposeMesh();
  }
}

